import { useEffect, useState } from "react"
import { motion } from "motion/react"

const HOLE_PADDING = 6
const HOLE_RADIUS = 10

/** Resolves one or more CSS selectors (a step can target several scattered elements —
 *  e.g. "your seats" is 2-4 seats around the table, not one rectangle) to their live
 *  `getBoundingClientRect()`s, recomputing on resize/scroll since the page is normally
 *  scrolled, not a fixed layout. Selectors that currently match nothing are simply
 *  skipped — the tour only reads whatever real state already exists, it never waits
 *  or retries indefinitely for a target that isn't there. */
export function useTourTargetRects(selectors: string[]): DOMRect[] {
  const [rects, setRects] = useState<DOMRect[]>([])

  useEffect(() => {
    const recompute = () => {
      const elements = selectors.flatMap((sel) => {
        try { return Array.from(document.querySelectorAll(sel)) } catch { return [] }
      })
      setRects(elements.map((el) => el.getBoundingClientRect()))
    }
    recompute()
    // One extra pass next frame — covers layout that settles just after mount (fonts, etc).
    const raf = requestAnimationFrame(recompute)
    window.addEventListener("resize", recompute)
    window.addEventListener("scroll", recompute, true)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener("resize", recompute)
      window.removeEventListener("scroll", recompute, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectors.join("|")])

  return rects
}

/** Full-viewport dark backdrop with a hole cut for each target rect (an SVG mask, since
 *  the single-box-shadow cutout trick only supports one hole) — a thin gold ring traces
 *  each hole so the highlighted item(s) pop. Rects animate between steps via `motion`. */
export function TourSpotlight({ rects }: { rects: DOMRect[] }) {
  return (
    <svg className="fixed inset-0 pointer-events-none" width="100%" height="100%" style={{ zIndex: 55 }}>
      <defs>
        <mask id="tour-spotlight-mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          {rects.map((r, i) => (
            <motion.rect
              key={i}
              initial={false}
              animate={{
                x: r.left - HOLE_PADDING,
                y: r.top - HOLE_PADDING,
                width: r.width + HOLE_PADDING * 2,
                height: r.height + HOLE_PADDING * 2,
              }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              rx={HOLE_RADIUS}
              fill="black"
            />
          ))}
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="rgba(4,2,1,0.86)" mask="url(#tour-spotlight-mask)" />
      {rects.map((r, i) => (
        <motion.rect
          key={i}
          initial={false}
          animate={{
            x: r.left - HOLE_PADDING,
            y: r.top - HOLE_PADDING,
            width: r.width + HOLE_PADDING * 2,
            height: r.height + HOLE_PADDING * 2,
          }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          rx={HOLE_RADIUS}
          fill="none"
          stroke="#C9A84C"
          strokeWidth={2}
          style={{ filter: "drop-shadow(0 0 6px rgba(201,168,76,0.7))" }}
        />
      ))}
    </svg>
  )
}
